import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useFetcher } from '../../graphql';

export interface ActiveCallForIssue {
  id: string;
  room: string;
  state: string;
}

/**
 * Polls for the active group call on an Issue (Activity) so a bystander's
 * Activity screen can show a "call in progress" pill. Polling (not a
 * subscription) keeps this in step with the resolver, which is a raw-db,
 * no-perm-gate lookup.
 */
export const useActiveCallForIssue = (
  issueId?: string,
  options?: { enabled?: boolean }
) => {
  const fetcherFn = useFetcher<
    { call: { activeCallForIssue: ActiveCallForIssue | null } },
    { issueId: string }
  >(ACTIVE_CALL_FOR_ISSUE);

  return useQuery({
    queryKey: ['activeCallForIssue', issueId],
    queryFn: async (): Promise<ActiveCallForIssue | null> => {
      const data = await fetcherFn({ issueId: issueId as string });
      return data.call.activeCallForIssue;
    },
    // Callers pass `enabled: false` for a signed-out viewer; the 3-second
    // poll would otherwise 403 on a loop for the whole visit.
    enabled: !!issueId && (options?.enabled ?? true),
    refetchInterval: 3000,
  });
};

/**
 * Imperative variant of {@link useActiveCallForIssue} for event-time checks —
 * e.g. CallProvider verifying that a stashed ring's call is still live before
 * presenting it. Returns a stable function (safe in effect/callback deps):
 * `useFetcher` mints a new closure every render, so it's read through a ref.
 */
export const useFetchActiveCallForIssue = () => {
  const fetcherFn = useFetcher<
    { call: { activeCallForIssue: ActiveCallForIssue | null } },
    { issueId: string }
  >(ACTIVE_CALL_FOR_ISSUE);
  const fetcherRef = useRef(fetcherFn);
  fetcherRef.current = fetcherFn;

  return useCallback(async (issueId: string): Promise<ActiveCallForIssue | null> => {
    const data = await fetcherRef.current({ issueId });
    return data.call.activeCallForIssue;
  }, []);
};

const ACTIVE_CALL_FOR_ISSUE = `
  query ACTIVE_CALL_FOR_ISSUE($issueId: ID!) {
    call {
      activeCallForIssue(issueId: $issueId) {
        id
        room
        state
      }
    }
  }
`;
